import React, { useCallback, useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { stopPropagation } from '../../utils/stop-propagation'

import styles from './click-activator.module.css'

const propTypes = {
    className: PropTypes.string,
    title: PropTypes.string,
    titleClassName: PropTypes.string
}

/**
 * Render a click activator to avoid unintended scrolling for child component wrapped with this overlay. Clicking on
 * this overlay will activate the underlying component. Clicking outside the overlay area will activate overlay.
 * Because this layer is positioned absolute make sure the parent container has a relative position.
 *
 * @see {@link https://confluence.ttt-sp.com/display/TTB/Atoms#Atoms-Click}
 *
 * @example <caption>Basic example</caption>
 * <code>
 *      // CSS
 *      .parent {
 *          width: 250px;
 *          height: 250px;
 *      }
 *
 *      // Javascript
 *      <div className={styles['parent']}>
 *          <ClickActivator title='Click to use map' >
 *              <T3Map />
 *          </ClickActivator>
 *      </div>
 * </code>
 *
 * @property {Object} props - The component properties
 * @property {string} [props.className] - CSS class for the dialog wrapper
 * @property {string} [props.title] - The title which is shown on hover
 * @property {string} [props.titleClassName] - CSS class for the title headline
 */
function ClickActivator({ children, className, title, titleClassName }) {
    const containerRef = useRef(null)
    const [showDialog, setShowDialog] = useState(true)

    const onShowModal = useCallback(
        event => {
            if (!showDialog && !containerRef?.current?.contains(event.target)) {
                setShowDialog(true)
            }
        },
        [containerRef, showDialog, setShowDialog]
    )

    const onClick = useCallback(() => {
        if (showDialog) {
            setShowDialog(false)
        }
    }, [showDialog, setShowDialog])

    useEffect(() => {
        document.addEventListener('mousedown', onShowModal)

        return () => {
            document.removeEventListener('mousedown', onShowModal)
        }
    }, [onShowModal])

    const classNameDialog = cx(styles['overlay'], className, 't3sel-click-activator')
    const classNameTitle = cx(styles['title'], titleClassName, 't3sel-click-activator-title')

    return (
        <div
            ref={containerRef}
            data-testid="dialog-container"
            className={cx(styles['container'], 't3sel-click-activator-container')}
        >
            {children}
            {showDialog && (
                <dialog
                    data-testid="dialog"
                    className={classNameDialog}
                    onClickCapture={onClick}
                    onContextMenuCapture={stopPropagation}
                    onDoubleClickCapture={stopPropagation}
                    onDragCapture={stopPropagation}
                    onDragEndCapture={stopPropagation}
                    onDragEnterCapture={stopPropagation}
                    onDragExitCapture={stopPropagation}
                    onDragLeaveCapture={stopPropagation}
                    onDragOverCapture={stopPropagation}
                    onDragStartCapture={stopPropagation}
                    onDropCapture={stopPropagation}
                    onMouseDownCapture={stopPropagation}
                    onMouseMoveCapture={stopPropagation}
                    onMouseOutCapture={stopPropagation}
                    onMouseOverCapture={stopPropagation}
                    onMouseUpCapture={stopPropagation}
                    onScrollCapture={stopPropagation}
                >
                    {title && <h3 className={classNameTitle}>{title}</h3>}
                </dialog>
            )}
        </div>
    )
}

ClickActivator.propTypes = propTypes

export { ClickActivator }
